import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from '../store/store';
import { UserInfo } from '../store/UserSlice';
import { TransactionService } from './TransactionService';
import { AuthService } from './AuthService';
import { loadSession, clearSession } from '../utils/session';

type dispatcher = ThunkDispatch<RootState, null, AnyAction>;

export interface ApiOptions {
    showAlert: boolean;
}

const clientBaseUrl = process.env.REACT_APP_CLIENT_API;

export class Api {
    private static dispatch: dispatcher | undefined;
    private static token: string | null;
    private static userId: number;
    private static userRole: string;
    private static organizationId: number;

    private static get options(): AxiosRequestConfig {
        return {
            headers: {
                Authorization: `Bearer ${Api.token}`,
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
                'userId' : String(Api.userId),
                'organizationId' : String(Api.organizationId),
                'userRole' : String(Api.userRole)
            },
        };
    }

    public static setDispatcher = (dispatcher: dispatcher): void => {
        Api.dispatch = dispatcher;
    };

    public static setToken = (token: string): void => {
        Api.token = token;
    };

    public static setUserInfo = (userInfo: UserInfo): void => {
        Api.userRole    = userInfo.role;
        Api.userId      = userInfo.id;
        this.setToken(userInfo.accessToken);
    };

    public static get = <T>(url: string, options?: ApiOptions): Promise<T> => {
        const target = Api.formatUrl(url);
        return axios.get<T>(target, Api.options)
            .then(Api.handleSuccess)
            .catch(Api.handleError('Get', options));
    }

    public static post = <T>(url: string, obj?: any, options?: ApiOptions): Promise<T> => {
        if (!Api.token) this.setToken(loadSession<{ accessToken?: string }>()?.accessToken || '')
        return axios.post<T>(Api.formatUrl(url), obj ?? {}, Api.options)
            .then(Api.handleSuccess)
            .catch(Api.handleError('Post'));
    }

    public static put = <T>(url: string, obj: any): Promise<T> =>
        axios.put<T>(Api.formatUrl(url), obj, Api.options)
            .then(Api.handleSuccess)
            .catch(Api.handleError('Put'));

    public static patch = <T>(url: string, obj: any): Promise<T> =>
        axios.patch<T>(Api.formatUrl(url), obj, Api.options)
            .then(Api.handleSuccess)
            .catch(Api.handleError('Patch'));

    public static delete = <T>(url: string): Promise<T> =>
        axios.delete<T>(Api.formatUrl(url), Api.options)
            .then(Api.handleSuccess)
            .catch(Api.handleError('Delete'));

    private static handleSuccess = <T>(res: AxiosResponse<T>): T => {
        return this.apiResponseFormatter(res.data);
    };

    private static apiResponseFormatter<T>(obj: T): T {
        if (!obj) return obj;


        // Array
        if (Array.isArray(obj)) {
            return obj.map(i => this.apiResponseFormatter(i)) as unknown as T;
        }

        const ISO_DATE_REGEX =/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
        // ISO date string
        if (typeof obj === 'string' && ISO_DATE_REGEX.test(obj)) {
            return new Date(obj).toLocaleString() as unknown as T;
        } 

        // Object
        if (typeof obj === 'object' && !(obj instanceof Date)) {
            const formatted: any = {};

            for (const key in obj) {
                formatted[key] = this.apiResponseFormatter(
                    (obj as any)[key]
                );
            }

            return formatted as T;
        }

        return obj;
    }

    private static handleError = (type: string, options: ApiOptions = { showAlert: true }) => (err: any): any => {

        if (process.env.NODE_ENV !== 'production') {
            console.error(`API - Error during ${type}`);
            console.error(err.response?.data);
        }

        if (!Api.dispatch) {
            console.error('error - no dispatch')
            throw new Error('Missing Dispatch, please use API.setDispatcher');
        }
 
        const errorResponse :any = err;
        if (errorResponse.error === 'expired_token') {
            AuthService.makePriceApiLogin();
        }       

        if (err?.response?.status === 403) {
            console.error('error - 403')
            //Api.dispatch(logoff());
            throw err.response?.data || err;
        }

        if (err?.response?.status === 401) {
            clearSession();
            window.location.href = '/';
            return;
        }

        if (options.showAlert) {
            console.error('show alert');
            //throw err.response?.data || err;
            //Api.dispatch(showAlert(getMessageFromError(err.response?.data), 'error'));
        }

        //console.log('error - handle error - general')
        throw err.response?.data || err;
    };

    private static formatUrl = (url: string): string => {
        /*
        const indexInterrogation = url.indexOf('?');
        if (indexInterrogation === -1) {
            let formattedUrl = url.replace(/\/$/, '');
            if(!url.includes('image'))
                formattedUrl = formattedUrl + '/';
            return formattedUrl;
        }

        const path = url.substr(0, indexInterrogation).replace(/\/$/, '') + '/';
        return path + url.substr(indexInterrogation);
        */
       return url;
    };

    public static async getInvesting() {
        try {
            const res = await axios.get('https://br.investing.com/currencies/usd-brl');
            const html = res.data as string;
            const regex = /data-test="instrument-price-last"[^>]*>([^<]+)<\/div>/;
            const match = html.match(regex);
            
            if (match && match[1]) {
                const priceText = match[1].trim();
                const price = parseFloat(priceText.replace(',', '.'));
                
                return {
                    raw: priceText,
                    value: price
                };
            }

        } catch (err) {
            console.error('Error fetching data:', err);
            throw err; // or handle the error as needed
        }
    }
}
